import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.wallet)

export const WALLET_INSTANCE = service.instanceKey

export const WALLET_ENDPOINTS = {
  WALLET:                   "/v1/wallets",
  KYC_VERIFY_BVN:           "/v1/wallets/kyc/verify-bvn",
  KYC_PROOF_OF_RESIDENCE:   "/v1/wallets/kyc/proof-of-residence",
  FUNDING_ACCOUNT:          "/v1/wallets/funding-account",
  BANKS:                    "/v1/wallets/banks",
  RESOLVE_ACCOUNT:          "/v1/wallets/resolve-account",
  RESOLVE_WALLET:           "/v1/wallets/resolve-wallet",
  INITIATE_BANK_TRANSFER:   "/v1/wallets/initiate_bank_transfer",
  PAYOUT_CHARGES:           "/v1/wallets/payout-charges",
  CONVERSION_RATE:          "/v1/wallets/conversion-rate",
  SWAP_CURRENCY:            "/v1/wallets/swap-currency",
  WALLET_SETTINGS:          "/v1/wallets/settings",
  TRANSFER_PURPOSES:        "/v1/wallets/transfer-purposes",
  AIRTIME_PURCHASE:         "/v1/wallets/airtime-purchase",
  DATA_PURCHASE:            "/v1/wallets/data-purchase",
  CABLE_PURCHASE:           "/v1/wallets/cable-purchase",
  ELECTRICITY_PURCHASE:     "/v1/wallets/electricity-purchase",
  CABLE_PROVIDERS:          "/v1/wallets/bill-payments/cable-providers",
  ELECTRICITY_PROVIDERS:    "/v1/wallets/bill-payments/electricity-providers",
  RESOLVE_CABLE_NUMBER:     "/v1/wallets/resolve-cable-number",
  RESOLVE_METER_NUMBER:     "/v1/wallets/resolve-meter-number",
  WALLET_CURRENCIES:        "/v1/misc/wallet-currencies",
} as const

export const walletService = {
  instanceKey: service.instanceKey,

  getWallet:               (walletId: string | number) =>
    service.get(`${WALLET_ENDPOINTS.WALLET}/${walletId}`),

  getBvnDetails:           (walletId: string | number) =>
    service.get(`${WALLET_ENDPOINTS.WALLET}/${walletId}/bvn-details`),

  updateBasicInfo:         (walletId: string | number, body: Record<string, any>) =>
    service.post(`${WALLET_ENDPOINTS.WALLET}/${walletId}/basic-info`, body),

  submitProofOfResidence:  (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.KYC_PROOF_OF_RESIDENCE, body),

  getFundingAccount:       (currency: string) =>
    service.get(`${WALLET_ENDPOINTS.FUNDING_ACCOUNT}/${currency}`),

  getPaymentTransactions:  (walletId: string | number, params?: Record<string, any>) =>
    service.get(
      `${WALLET_ENDPOINTS.WALLET}/${walletId}/payment-transactions`,
      { type: "bill-payment", ...params }
    ),

  initiateBankTransfer:    (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.INITIATE_BANK_TRANSFER, body),

  airtimePurchase:         (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.AIRTIME_PURCHASE, body),

  dataPurchase:            (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.DATA_PURCHASE, body),

  cablePurchase:           (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.CABLE_PURCHASE, body),

  electricityPurchase:     (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.ELECTRICITY_PURCHASE, body),

  getCableProviders:       () =>
    service.get(WALLET_ENDPOINTS.CABLE_PROVIDERS),

  getCablePlans:           (provider: string) =>
    service.get(`/v1/wallets/bill-payments/${provider}/cable-plans`),

  getCableAddons:          (body: Record<string, any>) =>
    service.post("/v1/wallets/bill-payments/cable-addons", body),

  resolveCableNumber:      (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.RESOLVE_CABLE_NUMBER, body),

  getElectricityProviders: () =>
    service.get(WALLET_ENDPOINTS.ELECTRICITY_PROVIDERS),

  resolveMeterNumber:      (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.RESOLVE_METER_NUMBER, body),

  getDataBundles:          (provider: string) =>
    service.get(`/v1/wallets/bill-payments/${provider}/data-bundles`),

  getWalletWithAccounts:   (walletId: string | number) =>
    service.get(`${WALLET_ENDPOINTS.WALLET}/${walletId}`, { populate: "virtualAccounts" }),

  getBeneficiaries:        (walletId: string | number, params?: Record<string, any>) =>
    service.get(`${WALLET_ENDPOINTS.WALLET}/${walletId}/transfer-beneficiaries`, params),

  addBeneficiary:          (walletId: string | number, body: Record<string, any>) =>
    service.post(`${WALLET_ENDPOINTS.WALLET}/${walletId}/transfer-beneficiaries`, body),

  deleteBeneficiary:       (walletId: string | number, beneficiaryId: string) =>
    service.delete(`${WALLET_ENDPOINTS.WALLET}/${walletId}/transfer-beneficiaries/${beneficiaryId}`),

  getTransactions:         (walletId: string | number, params?: Record<string, any>) =>
    service.get(`${WALLET_ENDPOINTS.WALLET}/${walletId}/transactions`, params),

  getBanks:                () =>
    service.get(WALLET_ENDPOINTS.BANKS),

  resolveAccount:          (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.RESOLVE_ACCOUNT, body),

  verifyBvn:               (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.KYC_VERIFY_BVN, body),

  setPin:                  (walletId: string | number, body: Record<string, any>) =>
    service.post(`${WALLET_ENDPOINTS.WALLET}/${walletId}/set-pin`, body),

  updatePin:               (walletId: string | number, body: { oldPin: string; pin: string }) =>
    service.post(`${WALLET_ENDPOINTS.WALLET}/${walletId}/update-pin`, body),

  getUserGroupBeneficiaries: (walletId: string | number, params?: Record<string, any>) =>
    service.get(`${WALLET_ENDPOINTS.WALLET}/${walletId}/user-group-beneficiaries`, params),

  resolveWallet:           (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.RESOLVE_WALLET, body),

  getWalletSettings:       () =>
    service.get(WALLET_ENDPOINTS.WALLET_SETTINGS),

  getConversionRate:       (params?: Record<string, any>) =>
    service.get(WALLET_ENDPOINTS.CONVERSION_RATE, params),

  getWalletCurrencies:     () =>
    service.get(WALLET_ENDPOINTS.WALLET_CURRENCIES),

  swapCurrency:            (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.SWAP_CURRENCY, body),

  getPayoutCharges:        (body: Record<string, any>) =>
    service.post(WALLET_ENDPOINTS.PAYOUT_CHARGES, body),

  getPaymentTransactionByReference: (reference: string) =>
    service.get(`${WALLET_ENDPOINTS.WALLET}/payment-transactions/${reference}`),

  getTransferPurposes:     () =>
    service.get(WALLET_ENDPOINTS.TRANSFER_PURPOSES),
}
